import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { MonthCell } from './MonthCell';
import { METRICS, fmtQty, fmtVal } from './fmt';

const th = 'px-2 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap';
const td = 'px-2 py-2 text-sm tabular-nums whitespace-nowrap text-right';
const MEDAL = { 1: 'bg-amber-400 text-white', 2: 'bg-slate-400 text-white', 3: 'bg-orange-400 text-white' };

const StaffCell = ({ r }) => (
  <td className="sticky left-0 z-10 bg-inherit px-3 py-2 min-w-[210px] border-r border-gray-100">
    <div className="flex items-center gap-2">
      <span className={`inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${MEDAL[r.rank] || 'bg-gray-100 text-gray-600'}`} data-testid={`rank-pos-${r.staff_id}`}>{r.rank ?? '–'}</span>
      <div className="min-w-0">
        <div className="font-medium text-gray-900 truncate" data-testid={`rank-name-${r.staff_id}`}>{r.staff_name}</div>
        <div className="text-xs text-gray-400 truncate" title={[r.designation, r.worker_type, r.gender, r.collar && `${r.collar} collar`, r.agency].filter(Boolean).join(' · ')}>
          {r.staff_id}{r.section ? ` · ${r.section}` : ''}{r.designation ? ` · ${r.designation}` : ''}
        </div>
        {(r.worker_type || r.collar || r.roll_status === 'left') && (
          <div className="flex gap-1 mt-0.5" data-testid={`rank-tags-${r.staff_id}`}>
            {r.worker_type && <span className={`rounded px-1.5 text-[10px] font-semibold ${r.worker_type === 'contract' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>{r.worker_type === 'contract' ? 'Contract' : 'Regular'}</span>}
            {r.collar && <span className={`rounded px-1.5 text-[10px] font-semibold ${r.collar === 'white' ? 'bg-sky-50 text-sky-700' : 'bg-blue-800 text-white'}`}>{r.collar === 'white' ? 'White' : 'Blue'}</span>}
            {r.roll_status === 'left' && <span className="rounded px-1.5 text-[10px] font-semibold bg-rose-100 text-rose-700">Left</span>}
          </div>
        )}
      </div>
    </div>
  </td>
);

const Flag = ({ r, need }) => {
  if (r.repeat_low) return <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white px-2 py-0.5 text-xs font-semibold" data-testid={`rank-repeat-${r.staff_id}`}><AlertTriangle className="w-3 h-3" />{r.max_low_streak} months in a row</span>;
  if (r.current_low_streak > 0) return <span className="rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs">Low {r.current_low_streak}/{need}</span>;
  return null;
};

const Row = ({ r, months, metric, need }) => (
  <tr className={`border-t border-gray-100 ${r.repeat_low ? 'bg-rose-50' : 'bg-white'} hover:bg-indigo-50/40`} data-testid={`rank-row-${r.staff_id}`}>
    <StaffCell r={r} />
    <td className={td}>{r.days}</td>
    <td className={td}>{r.hours_est ? '~' : ''}{r.hours}</td>
    <td className={td}>{fmtQty(r.qty)}</td>
    {Object.keys(METRICS).map((k) => (
      <td key={k} className={`${td} ${k === metric ? 'font-bold text-indigo-700 bg-indigo-50/60' : ''}`} data-testid={`rank-${k}-${r.staff_id}`}>{fmtVal(r[k], k)}</td>
    ))}
    <td className={`${td} ${r.low ? 'text-rose-600 font-semibold' : 'text-gray-600'}`} data-testid={`rank-vsavg-${r.staff_id}`}>{r.vs_avg_pct != null ? `${r.vs_avg_pct}%` : '—'}</td>
    {months.map((m) => <MonthCell key={m.key} cell={r.months[m.key]} metric={metric} testId={`rank-month-${r.staff_id}-${m.key}`} />)}
    <td className={`${td} text-center`} data-testid={`rank-lowmonths-${r.staff_id}`}>{r.low_months || ''}</td>
    <td className="px-2 py-2"><Flag r={r} need={need} /></td>
  </tr>
);

const FootRow = ({ label, months, render, testId }) => (
  <tr className="border-t border-gray-200 bg-gray-50 text-xs" data-testid={testId}>
    <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 font-semibold text-gray-600 border-r border-gray-100">{label}</td>
    <td colSpan={7} />
    {months.map((m) => <td key={m.key} className="px-2 py-2 text-center border-l border-gray-100 whitespace-nowrap">{render(m.key)}</td>)}
    <td colSpan={2} />
  </tr>
);

export const RankingsTable = ({ op, rows, months, metric, need }) => {
  const ms = op.months || {};
  const who = (s) => (s ? <span title={s.staff_id}>{s.staff_name} <b className="tabular-nums">{fmtVal(s.value, metric)}</b></span> : '—');
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto" data-testid="rank-table-wrap">
      <table className="min-w-full border-collapse" data-testid="rank-table">
        <thead className="bg-gray-50">
          <tr>
            <th className={`${th} sticky left-0 z-20 bg-gray-50 text-left px-3`}>Rank · Staff</th>
            <th className={`${th} text-right`}>Days</th><th className={`${th} text-right`}>Hours</th><th className={`${th} text-right`}>Pieces</th>
            {Object.entries(METRICS).map(([k, m]) => <th key={k} className={`${th} text-right ${k === metric ? 'text-indigo-700' : ''}`}>{m.short}</th>)}
            <th className={`${th} text-right`}>vs Avg</th>
            {months.map((m) => (
              <th key={m.key} className={`${th} text-center border-l border-gray-200`} data-testid={`rank-month-head-${m.key}`}>
                <div className="text-gray-700">{m.label}</div>
                <div className="font-normal text-gray-400">avg {fmtVal(ms[m.key]?.avg, metric)}{ms[m.key]?.low_count ? ` · ${ms[m.key].low_count} low` : ''}</div>
              </th>
            ))}
            <th className={`${th} text-center`}>Low months</th><th className={`${th} text-left`}>Flag</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <Row key={r.staff_id} r={r} months={months} metric={metric} need={need} />)}
          {!rows.length && <tr><td colSpan={10 + months.length} className="px-4 py-10 text-center text-gray-400" data-testid="rank-empty">No staff match</td></tr>}
        </tbody>
        <tfoot>
          <FootRow label="Operation average" months={months} testId="rank-foot-avg" render={(k) => <b className="tabular-nums">{fmtVal(ms[k]?.avg, metric)}</b>} />
          <FootRow label="Highest" months={months} testId="rank-foot-top" render={(k) => <span className="text-emerald-700">{who(ms[k]?.top)}</span>} />
          <FootRow label="Lowest" months={months} testId="rank-foot-bottom" render={(k) => <span className="text-rose-700">{who(ms[k]?.bottom)}</span>} />
        </tfoot>
      </table>
    </div>
  );
};

export default RankingsTable;
